import { useCallback, useEffect, useState } from 'react';
import { Card, EmptyState } from '@/shared/ui/atoms';
import { externalAccountApi, internalInfraAccountApi, type createAccountVaultApi } from '@/features/external-account/api';
import type { ExternalAccount, ExternalCredential } from '@/features/external-account/types';

const INPUT = 'w-full rounded-md border border-line px-2.5 py-1.5 text-[13px]';
const LINK = 'cursor-pointer text-[11.5px] hover:underline disabled:cursor-not-allowed disabled:opacity-40';

/** 한 줄이 지금 무엇을 보여 주는가. 평문은 "보기"·"수정"을 누른 줄에만, 화면 메모리에만 있다. */
type RowState =
  | { mode: 'revealed'; credential: ExternalCredential }
  | { mode: 'editing'; service: string; link: string; credential: ExternalCredential };

/**
 * 외부 계정 — 콘솔 밖 서비스에 로그인할 아이디·비밀번호를 등록하고 꺼내 본다.
 *
 * 목록에는 서비스 이름·링크만 온다. 아이디·비밀번호는 "보기"를 누른 줄만 서버에서 암호문으로 받아 이
 * 화면에서 푼다 — 화면을 여는 것만으로 모든 계정이 오가지 않게.
 */
export function ExternalAccountView(): React.JSX.Element {
  return <AccountVaultView api={externalAccountApi} placeholder="서비스 (예: GitHub, 포트원, 팝빌)" />;
}

/** 내부 인프라 계정 — DB·GCP·서버처럼 사내 인프라에 들어갈 아이디·비밀번호. 외부 계정과 같은 보관함이되 메뉴 권한이 따로다. */
export function InternalInfraAccountView(): React.JSX.Element {
  return <AccountVaultView api={internalInfraAccountApi} placeholder="인프라 (예: 운영 Cloud SQL)" />;
}

function AccountVaultView({ api, placeholder }: { api: ReturnType<typeof createAccountVaultApi>; placeholder: string }): React.JSX.Element {
  const [accounts, setAccounts] = useState<ExternalAccount[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState({ service: '', link: '', loginId: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState<Record<number, RowState>>({});

  const load = useCallback(() => {
    api
      .list()
      .then((payload) => {
        setAccounts(payload.accounts);
        setError(null);
      })
      .catch((cause: Error) => setError(cause.message));
  }, [api]);

  useEffect(load, [load]);

  const setRow = useCallback((id: number, state: RowState | null) => {
    setRows((current) => {
      const next = { ...current };
      if (state) next[id] = state;
      else delete next[id];
      return next;
    });
  }, []);

  const create = useCallback(() => {
    setBusy(true);
    api
      .create(draft.service.trim(), draft.link.trim(), { loginId: draft.loginId.trim(), password: draft.password })
      .then(() => {
        setDraft({ service: '', link: '', loginId: '', password: '' });
        load();
      })
      .catch((cause: Error) => setError(cause.message))
      .finally(() => setBusy(false));
  }, [api, draft, load]);

  const reveal = useCallback(
    (account: ExternalAccount, then: (credential: ExternalCredential) => RowState) => {
      api
        .reveal(account.id)
        .then((credential) => setRow(account.id, then(credential)))
        .catch((cause: Error) => setError(cause.message));
    },
    [setRow],
  );

  const save = useCallback(
    (account: ExternalAccount, state: Extract<RowState, { mode: 'editing' }>) => {
      api
        .update(account.id, state.service.trim(), state.link.trim(), { loginId: state.credential.loginId.trim(), password: state.credential.password })
        .then(() => {
          setRow(account.id, null);
          load();
        })
        .catch((cause: Error) => setError(cause.message));
    },
    [api, load, setRow],
  );

  const remove = useCallback(
    (account: ExternalAccount) => {
      if (!window.confirm(`"${account.service}" 계정을 지운다. 지운 비밀번호는 되찾을 수 없다.`)) return;
      api
        .remove(account.id)
        .then(() => {
          setRow(account.id, null);
          load();
        })
        .catch((cause: Error) => setError(cause.message));
    },
    [api, load, setRow],
  );

  const ready = draft.service.trim() !== '' && draft.loginId.trim() !== '' && draft.password !== '';

  return (
    <div className="flex flex-col gap-3">
      <Card title="계정 등록" hint="비밀번호는 암호화해 저장하고, 아이디·비밀번호는 오갈 때도 암호화된다">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
          <input
            value={draft.service}
            onChange={(event) => setDraft({ ...draft, service: event.target.value })}
            placeholder={placeholder}
            className={INPUT}
          />
          <input
            value={draft.link}
            onChange={(event) => setDraft({ ...draft, link: event.target.value })}
            placeholder="링크 (https://…, 선택)"
            autoComplete="off"
            className={INPUT}
          />
          <input
            value={draft.loginId}
            onChange={(event) => setDraft({ ...draft, loginId: event.target.value })}
            placeholder="아이디"
            autoComplete="off"
            className={INPUT}
          />
          <PasswordInput value={draft.password} onChange={(password) => setDraft({ ...draft, password })} />
        </div>
        <button
          type="button"
          onClick={create}
          disabled={busy || !ready}
          className="mt-3 cursor-pointer rounded-md bg-accent px-3 py-1.5 text-[12.5px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          등록
        </button>
      </Card>

      {error && <EmptyState message={`실패 — ${error}`} />}

      <Card title="등록된 계정" hint="보기를 누른 줄만 아이디·비밀번호를 받아 온다">
        {!accounts ? (
          <EmptyState message="계정을 불러오는 중…" />
        ) : accounts.length === 0 ? (
          <EmptyState message="등록된 계정이 없다." />
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="[&>th]:border-b [&>th]:border-line [&>th]:px-2.5 [&>th]:py-2 [&>th]:text-left [&>th]:text-xs [&>th]:font-semibold [&>th]:text-muted">
                <th>서비스</th>
                <th>링크</th>
                <th>아이디</th>
                <th>비밀번호</th>
                <th className="w-[140px]">수정</th>
                <th className="w-[130px]" />
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => {
                const state = rows[account.id];
                return (
                  <tr
                    key={account.id}
                    className="border-b border-line-soft last:border-0 [&>td]:px-2.5 [&>td]:py-2 [&>td]:align-middle [&>td]:text-[12.5px]"
                  >
                    {state?.mode === 'editing' ? (
                      <>
                        <td>
                          <input
                            value={state.service}
                            onChange={(event) => setRow(account.id, { ...state, service: event.target.value })}
                            className={INPUT}
                          />
                        </td>
                        <td>
                          <input
                            value={state.link}
                            onChange={(event) => setRow(account.id, { ...state, link: event.target.value })}
                            placeholder="https://…"
                            autoComplete="off"
                            className={INPUT}
                          />
                        </td>
                        <td>
                          <input
                            value={state.credential.loginId}
                            onChange={(event) =>
                              setRow(account.id, { ...state, credential: { ...state.credential, loginId: event.target.value } })
                            }
                            autoComplete="off"
                            className={INPUT}
                          />
                        </td>
                        <td>
                          <PasswordInput
                            value={state.credential.password}
                            onChange={(password) => setRow(account.id, { ...state, credential: { ...state.credential, password } })}
                          />
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{account.service}</td>
                        <td>
                          {account.link && (
                            <a href={account.link} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                              열기
                            </a>
                          )}
                        </td>
                        <td>{state ? <Secret value={state.credential.loginId} /> : <Hidden />}</td>
                        <td>{state ? <Secret value={state.credential.password} mono /> : <Hidden />}</td>
                      </>
                    )}
                    <td className="text-[11.5px] text-muted">{localTime(account.updatedAt)}</td>
                    <td>
                      <span className="flex flex-wrap gap-2">
                        {state?.mode === 'editing' ? (
                          <>
                            <button
                              type="button"
                              onClick={() => save(account, state)}
                              disabled={state.service.trim() === '' || state.credential.loginId.trim() === '' || state.credential.password === ''}
                              className={`${LINK} text-accent`}
                            >
                              저장
                            </button>
                            <button type="button" onClick={() => setRow(account.id, null)} className={`${LINK} text-muted`}>
                              취소
                            </button>
                          </>
                        ) : (
                          <>
                            {state ? (
                              <button type="button" onClick={() => setRow(account.id, null)} className={`${LINK} text-muted`}>
                                숨기기
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => reveal(account, (credential) => ({ mode: 'revealed', credential }))}
                                className={`${LINK} text-accent`}
                              >
                                보기
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() =>
                                state
                                  ? setRow(account.id, { mode: 'editing', service: account.service, link: account.link ?? '', credential: state.credential })
                                  : reveal(account, (credential) => ({ mode: 'editing', service: account.service, link: account.link ?? '', credential }))
                              }
                              className={`${LINK} text-accent`}
                            >
                              수정
                            </button>
                            <button type="button" onClick={() => remove(account)} className={`${LINK} text-rose-700`}>
                              삭제
                            </button>
                          </>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

/** 비밀번호 입력 — 기본은 가리고, 오타를 확인할 수 있게 잠깐 펼쳐 볼 수 있다. */
function PasswordInput({ value, onChange }: { value: string; onChange: (value: string) => void }): React.JSX.Element {
  const [shown, setShown] = useState(false);
  return (
    <span className="flex items-center gap-1.5">
      <input
        type={shown ? 'text' : 'password'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="비밀번호"
        // 브라우저가 콘솔 로그인 비밀번호로 착각해 저장·자동완성하지 않게 한다.
        autoComplete="new-password"
        className={INPUT}
      />
      <button type="button" onClick={() => setShown(!shown)} className={`${LINK} shrink-0 text-muted`}>
        {shown ? '가리기' : '표시'}
      </button>
    </span>
  );
}

/** 풀어 받은 값 한 칸 — 그대로 보여 주고 복사할 수 있게 한다. */
function Secret({ value, mono }: { value: string; mono?: boolean }): React.JSX.Element {
  return (
    <span className="flex items-center gap-1.5">
      <code className={`rounded border border-line bg-surface px-1.5 py-0.5 text-[12px] break-all ${mono ? 'font-mono' : ''}`}>{value}</code>
      <button type="button" onClick={() => void navigator.clipboard.writeText(value)} className={`${LINK} shrink-0 text-muted`}>
        복사
      </button>
    </span>
  );
}

function Hidden(): React.JSX.Element {
  return <span className="text-faint tracking-widest">••••••</span>;
}

function localTime(iso: string): string {
  return new Date(iso).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' });
}
